import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';
import { morph } from '@theme/morph';

/**
 * A custom element that powers the SPV main-product section.
 *
 * Responsibilities:
 * - Variant card clicks: proxy the click into the matching hidden option-1
 *   radio inside the section's <variant-picker>. The variant-picker handles
 *   the rest (fetches the new section HTML, dispatches VariantSelected /
 *   VariantUpdate).
 * - Group card clicks: when the card points at a different product in the
 *   product_group, fetch that product's main-product section via the
 *   Section Rendering API, swap the inner content of this element, and
 *   pushState the new URL — no full reload.
 * - VariantUpdate: when the variant changes, re-sync the cards' `data-selected`
 *   and `aria-checked` attributes so the right card stays highlighted (and its
 *   expanded benefits panel stays visible). CTA price + hidden input are
 *   morphed by <product-form-component>; the optional header price line
 *   (.spv-main-product__price-line) is morphed here from the fresh section HTML.
 *
 * @extends {Component}
 */
class SpvMainProduct extends Component {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;
    const section = this.closest('.shopify-section') ?? this;
    section.addEventListener(ThemeEvents.variantUpdate, this.#onVariantUpdate, { signal });
    this.addEventListener('click', this.#onCardClick, { signal });
    window.addEventListener('resize', this.#onResize, { signal });
    window.addEventListener('popstate', this.#onPopState, { signal });

    // Mark the initial history entry with our swap scope so popstate can
    // distinguish back-navigation into a previously-swapped state from
    // unrelated history events.
    const initialHandle = this.#handleFromPath(window.location.pathname);
    if (initialHandle && !history.state?.spvProductSwap) {
      history.replaceState(
        { ...(history.state ?? {}), spvProductSwap: initialHandle },
        '',
        window.location.href
      );
    }

    // Initial sync: set the inline max-height on each benefits wrap so the
    // server-selected card's panel is visible at its true content height
    // without a first-paint animation jump. We disable the transition for
    // this measurement-and-set, then re-enable on the next frame.
    this.#syncBenefitsHeights({ skipTransition: true });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * Handles a click anywhere inside the component and routes it if it
   * happened on a card button.
   *
   * @param {MouseEvent} event
   */
  #onCardClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    // Group-product cards link to a sibling product. Intercept the anchor
    // navigation and AJAX-swap the section instead.
    const groupCard = /** @type {HTMLElement | null} */ (
      target.closest('[data-group-product-handle]')
    );
    if (groupCard && this.contains(groupCard)) {
      // Don't hijack modifier-clicks or non-left buttons; let the browser
      // open the link in a new tab / window as the user intended.
      const me = /** @type {MouseEvent} */ (event);
      if (me.metaKey || me.ctrlKey || me.shiftKey || me.altKey || me.button > 0) return;
      event.preventDefault();
      // The currently-selected card is the current product — clicking it
      // would re-fetch the same page. No-op instead.
      if (groupCard.dataset.selected === 'true') return;
      const handle = groupCard.dataset.groupProductHandle;
      if (handle) this.#swapToProduct(handle, { updateHistory: true });
      return;
    }

    const card = /** @type {HTMLElement | null} */ (target.closest('.spv-main-product__card[data-option-value-id]'));
    if (!card || !this.contains(card)) return;
    const optionValueId = card.dataset.optionValueId;
    if (!optionValueId) return;

    const radio = /** @type {HTMLInputElement | null} */ (
      this.querySelector(
        `variant-picker fieldset[data-fieldset-index="0"] input[type="radio"][data-option-value-id="${CSS.escape(optionValueId)}"]`
      )
    );

    if (!radio) return;

    // Optimistically reflect the new selection in the cards so the UI feels
    // instant; the VariantUpdate listener below re-syncs once the picker has
    // settled.
    this.#syncCardsToOptionValueId(optionValueId);

    if (!radio.checked) {
      radio.click();
    }
  }

  /**
   * Re-syncs `data-selected` + `aria-checked` on every card to match
   * the given option-value id, then re-measures the benefits wraps so the
   * collapse/expand transitions are symmetric.
   * @param {string} optionValueId
   */
  #syncCardsToOptionValueId(optionValueId) {
    const cards = this.querySelectorAll('[data-option-value-id]');
    for (const card of cards) {
      const matches = /** @type {HTMLElement} */ (card).dataset.optionValueId === optionValueId;
      /** @type {HTMLElement} */ (card).dataset.selected = matches ? 'true' : 'false';
      card.setAttribute('aria-checked', matches ? 'true' : 'false');
    }
    this.#syncBenefitsHeights();
  }

  /**
   * For each card with a benefits wrap, set inline max-height to the content's
   * actual scrollHeight when selected and to 0 when not. Driving both states
   * to the same end value gives the opening and closing transitions an
   * identical visible range, so switching cards reads as one smooth swap.
   *
   * @param {{ skipTransition?: boolean, root?: ParentNode }} [opts]
   */
  #syncBenefitsHeights = (opts = {}) => {
    const root = opts.root ?? this;
    const wraps = /** @type {NodeListOf<HTMLElement>} */ (
      root.querySelectorAll('.spv-main-product__card-benefits-wrap')
    );
    if (!wraps.length) return;

    if (opts.skipTransition) {
      for (const wrap of wraps) wrap.style.transition = 'none';
    }

    for (const wrap of wraps) {
      const card = /** @type {HTMLElement | null} */ (
        wrap.closest('.spv-main-product__card')
      );
      const selected = card?.dataset.selected === 'true';
      if (selected) {
        const inner = /** @type {HTMLElement | null} */ (
          wrap.querySelector('.spv-main-product__card-benefits')
        );
        const h = inner ? inner.scrollHeight : wrap.scrollHeight;
        wrap.style.maxHeight = `${h}px`;
      } else {
        wrap.style.maxHeight = '0px';
      }
    }

    if (opts.skipTransition) {
      // Force a reflow then restore the transition on the next frame so the
      // initial measurement doesn't animate.
      // eslint-disable-next-line no-unused-expressions
      void this.offsetHeight;
      requestAnimationFrame(() => {
        for (const wrap of wraps) wrap.style.transition = '';
      });
    }
  };

  #onResize = () => {
    // Content height can change with viewport (e.g. label wraps on small
    // viewports). Keep the open card sized to its current content.
    this.#syncBenefitsHeights({ skipTransition: true });
  };

  /**
   * Swaps the section content to a sibling product fetched via the Section
   * Rendering API. Keeps the custom element instance alive so listeners
   * remain bound.
   *
   * @param {string} handle - Sibling product handle.
   * @param {{ updateHistory?: boolean }} [opts]
   */
  #swapToProduct = async (handle, opts = {}) => {
    const sectionId = this.dataset.sectionId;
    if (!sectionId) {
      window.location.href = `/products/${handle}`;
      return;
    }

    // Optimistic UI: mark the clicked card as selected and unselect the others
    // so the swap feels instant even before the fetch resolves.
    this.#syncGroupCardsToHandle(handle);

    try {
      // Full-page fetch (not `?section_id=...`) so we also pick up the Kaching
      // Bundles app-embed scripts (deal-block-settings, product data). Those
      // live OUTSIDE our section, so a section-only response leaves the live
      // document holding the previous product's deal config and the bridge
      // (assets/spv-gift-with-purchase.js) reads stale data at ATC time.
      const response = await fetch(`/products/${handle}`, {
        credentials: 'same-origin',
        headers: { Accept: 'text/html' },
      });
      if (!response.ok) throw new Error(`Section fetch failed: ${response.status}`);
      const html = await response.text();

      const doc = new DOMParser().parseFromString(html, 'text/html');
      const newEl = /** @type {HTMLElement | null} */ (doc.querySelector('spv-main-product'));
      if (!newEl) throw new Error('Sibling section missing <spv-main-product>');

      let newProductId = '';
      // All visual mutations live inside this callback so the View Transition
      // (when supported) snapshots a clean before/after pair. The named
      // `.spv-main-product__gallery` (see section stylesheet) cross-fades;
      // everything else animates as root.
      const applyMutations = () => {
        // Snapshot the live group picker before the innerHTML overwrite so
        // its DOM (and any in-flight CSS transitions) can survive the swap.
        const oldGroupPicker = /** @type {HTMLElement | null} */ (
          this.querySelector('[data-spv-group-picker]')
        );

        this.innerHTML = newEl.innerHTML;

        // Mirror identifying data attributes so the existing variantUpdate
        // filter (which scopes by data-product-id) keeps matching the right
        // product after the swap.
        newProductId = newEl.dataset.productId ?? '';
        if (newProductId) this.dataset.productId = newProductId;

        // Restore the saved group picker if the sibling product surfaces the
        // same group (same ordered handle list). When the lists differ we
        // keep the freshly-rendered picker — correctness over animation.
        let groupPickerPreserved = false;
        if (oldGroupPicker) {
          const liveNewGroupPicker = /** @type {HTMLElement | null} */ (
            this.querySelector('[data-spv-group-picker]')
          );
          if (liveNewGroupPicker && this.#sameGroupHandles(oldGroupPicker, liveNewGroupPicker)) {
            liveNewGroupPicker.replaceWith(oldGroupPicker);
            groupPickerPreserved = true;
          }
        }

        // Variant cards are a fresh paint: size their benefits panels without
        // animation so we don't see a first-frame jump. Scope by the variant
        // UL so the preserved group cards aren't reset to skipTransition.
        const variantCardsRoot = this.querySelector('[data-spv-cards]');
        if (variantCardsRoot) {
          this.#syncBenefitsHeights({ root: variantCardsRoot, skipTransition: true });
        }

        if (groupPickerPreserved) {
          // Optimistic sync at the start of the swap already toggled
          // data-selected and kicked off transitions; this idempotent call
          // re-asserts the correct state in case anything mutated the DOM
          // mid-fetch.
          this.#syncGroupCardsToHandle(handle);
        } else {
          // Fresh group picker (or none) — size without animation, same as
          // variant cards.
          const groupCardsRoot = this.querySelector('[data-spv-group-cards]');
          if (groupCardsRoot) {
            this.#syncBenefitsHeights({ root: groupCardsRoot, skipTransition: true });
          }
        }
      };

      // View Transitions API: progressive enhancement. Browsers without
      // support (Firefox at time of writing) get the same DOM mutations
      // without the cross-fade.
      if (typeof document.startViewTransition === 'function') {
        document.startViewTransition(applyMutations);
      } else {
        applyMutations();
      }

      const newTitle = /** @type {HTMLElement | null} */ (
        doc.querySelector('.spv-main-product__title')
      );
      if (newTitle?.textContent) document.title = newTitle.textContent.trim();

      // Refresh Kaching app-embed scripts BEFORE the swap event fires so the
      // bridge's neutralizer + atomic-ATC handler read the new product's deal
      // config instead of the previous one.
      this.#syncKachingBundlesScripts(doc);

      if (opts.updateHistory !== false) {
        history.pushState(
          { ...(history.state ?? {}), spvProductSwap: handle },
          '',
          `/products/${handle}`
        );
      }

      document.dispatchEvent(
        new CustomEvent('spv:group-swap', { detail: { handle, productId: newProductId } })
      );
    } catch (_err) {
      // Fall back to a real navigation so the user is never stranded on a
      // half-swapped page.
      window.location.href = `/products/${handle}`;
    }
  };

  /**
   * Reflects the active group selection in the rendered cards: marks the
   * card matching `handle` as selected and the others as not. No-op when
   * no group cards are present.
   *
   * @param {string} handle
   */
  #syncGroupCardsToHandle(handle) {
    const cards = this.querySelectorAll('[data-spv-group-cards] [data-group-product-handle]');
    for (const card of cards) {
      const el = /** @type {HTMLElement} */ (card);
      const matches = el.dataset.groupProductHandle === handle;
      el.dataset.selected = matches ? 'true' : 'false';
      el.setAttribute('aria-checked', matches ? 'true' : 'false');
      if (matches) {
        el.setAttribute('aria-current', 'page');
      } else {
        el.removeAttribute('aria-current');
      }
    }
    // Match the variant-card flow: kick off the benefits-panel height
    // transition at the same moment as the background/border transitions,
    // so the selection animation reads as one synchronized swap.
    const groupCardsRoot = this.querySelector('[data-spv-group-cards]');
    if (groupCardsRoot) {
      this.#syncBenefitsHeights({ root: groupCardsRoot });
    }
  }

  /**
   * Replaces every `script.kaching-bundles-*` tag in the live document with
   * the corresponding tag from the fetched product page. These are app-embed
   * scripts (deal-block-settings, kaching-bundles-product) injected outside
   * any section, so a section-only swap leaves them holding stale config —
   * the bridge then reads the wrong deal at ATC time (or skips entirely on
   * non-trigger → trigger swaps). Removes orphans so a swap into a no-deal
   * product correctly returns the bridge to its skip path.
   *
   * @param {Document} doc - Fully-parsed product page from the fetch above.
   */
  #syncKachingBundlesScripts(doc) {
    const SELECTOR = 'script[class*="kaching-bundles-"]';
    for (const live of document.querySelectorAll(SELECTOR)) live.remove();
    for (const fresh of doc.querySelectorAll(SELECTOR)) {
      const clone = document.createElement('script');
      for (const { name, value } of Array.from(fresh.attributes)) {
        clone.setAttribute(name, value);
      }
      clone.textContent = fresh.textContent;
      document.body.appendChild(clone);
    }
  }

  /**
   * Compares the ordered list of `data-group-product-handle` values between
   * two group-picker roots. Used to decide whether the live picker can be
   * preserved across a swap or must be replaced with freshly-rendered HTML.
   *
   * @param {ParentNode} a
   * @param {ParentNode} b
   * @returns {boolean}
   */
  #sameGroupHandles(a, b) {
    const handlesOf = (root) =>
      Array.from(root.querySelectorAll('[data-group-product-handle]')).map(
        (el) => /** @type {HTMLElement} */ (el).dataset.groupProductHandle
      );
    const ha = handlesOf(a);
    const hb = handlesOf(b);
    if (ha.length !== hb.length) return false;
    for (let i = 0; i < ha.length; i++) if (ha[i] !== hb[i]) return false;
    return true;
  }

  /**
   * Derives a product handle from a `/products/<handle>` URL path. Returns
   * null when the path is not a product URL.
   *
   * @param {string} pathname
   * @returns {string | null}
   */
  #handleFromPath(pathname) {
    const match = /\/products\/([^/?#]+)/.exec(pathname);
    return match ? match[1] : null;
  }

  /**
   * @param {PopStateEvent} event
   */
  #onPopState = (event) => {
    // Only react to history entries we marked as our own swap scope.
    const handle = event.state?.spvProductSwap;
    if (!handle) return;
    if (handle === this.#handleFromPath(window.location.pathname)) {
      this.#swapToProduct(handle, { updateHistory: false });
    }
  };

  /**
   * @param {Event} event
   */
  #onVariantUpdate = (event) => {
    const detail = /** @type {any} */ (event).detail;
    if (!detail) return;

    const productId = detail.data?.productId;
    if (productId != null && String(productId) !== this.dataset.productId) return;

    // Live-update the optional header price line from the freshly rendered
    // section HTML (only present when the merchant enabled `show_price`).
    const freshHtml = detail.data?.html;
    if (freshHtml) {
      const currentPriceLine = this.querySelector('.spv-main-product__price-line');
      const newPriceLine = freshHtml.querySelector?.('.spv-main-product__price-line');
      if (currentPriceLine && newPriceLine) morph(currentPriceLine, newPriceLine);
    }

    // Find the option-1 radio that's now checked (set by variant-picker.js
    // during its morph). Fallback: read the source option value id.
    const checkedRadio = /** @type {HTMLInputElement | null} */ (
      this.querySelector(
        'variant-picker fieldset[data-fieldset-index="0"] input[type="radio"][data-current-checked="true"]'
      )
    );

    const optionValueId = checkedRadio?.dataset.optionValueId ?? detail.resource?.option_value_ids?.[0];
    if (optionValueId) {
      this.#syncCardsToOptionValueId(String(optionValueId));
    } else {
      // No option-value id but we still need to keep the benefits panel
      // sized correctly after a morph re-renders the markup.
      this.#syncBenefitsHeights({ skipTransition: true });
    }
  };
}

if (!customElements.get('spv-main-product')) {
  customElements.define('spv-main-product', SpvMainProduct);
}
